import type {
  ActivityFeedEntry,
  AIConversation,
  Channel,
  ConversationEntry,
  CustomerRecord,
  StudioId,
} from '@/domain/types';
import type { SequenceState } from '@/domain/sequenceFlow';

export type ConversationStatus =
  | 'live-call'
  | 'ringing-in'
  | 'ai-handoff'
  | 'ai-active'
  | 'voicemail'
  | 'missed-call'
  | 'awaiting-agent'
  | 'awaiting-customer'
  | 'resolved';

export interface ConversationHandler {
  kind: 'ai-intake' | 'ai-voice' | 'agent' | 'unassigned';
  /** Display name (e.g. "Lydia", "AI Lead Intake"). */
  label: string;
}

export interface ConversationFeedItem {
  customerId: string;
  customer: CustomerRecord;
  latestEntry: ConversationEntry | undefined;
  latestChannel: Channel;
  status: ConversationStatus;
  handler: ConversationHandler;
  lastActivityIso: string;
  /** Brief one-line preview of the latest message. */
  preview: string;
  /** True if the latest inbound message has not been responded to. */
  awaitingResponse: boolean;
  /** Studio context for filter. */
  studioId: StudioId;
  /**
   * True when a booking agent owns this thread (a sequence record exists). The
   * messaging team sees these greyed and filtered out of their default view.
   */
  inQueue: boolean;
}

interface BuildOpts {
  customers: Record<string, CustomerRecord>;
  aiConversations: Record<string, AIConversation>;
  activityFeed: ActivityFeedEntry[];
  /** Optional active call (live or ringing). Pass `getActiveCall()` from callFlow. */
  activeCall?: { customerId: string; status: string } | null;
  /** Optional UIStore.incomingCalls list — used to mark ringing rows. */
  incomingCalls?: Array<{ customerId: string }>;
  /** Threshold below which a conversation is considered "active" (default 7 days). */
  activeWithinMs?: number;
  /** Per-customer sequence records; presence marks a thread as owned by a booking agent. */
  sequenceState?: Record<string, SequenceState>;
  nowIso: string;
}

const STATUS_PRIORITY: Record<ConversationStatus, number> = {
  'live-call': 0,
  'ringing-in': 1,
  'ai-handoff': 2,
  voicemail: 3,
  'missed-call': 4,
  'ai-active': 5,
  'awaiting-agent': 6,
  'awaiting-customer': 7,
  resolved: 8,
};

function clip(text: string, max: number): string {
  if (text.length <= max) return text;
  return text.slice(0, max - 1).trimEnd() + '…';
}

function isVoicemail(entry: ConversationEntry): boolean {
  if (entry.channel !== 'voice') return false;
  return /voicemail/i.test(entry.body);
}

export function buildConversationsFeed(opts: BuildOpts): ConversationFeedItem[] {
  const {
    customers,
    aiConversations,
    activityFeed,
    activeCall,
    incomingCalls = [],
    activeWithinMs = 7 * 24 * 60 * 60 * 1000,
    sequenceState = {},
    nowIso,
  } = opts;

  const now = new Date(nowIso).getTime();

  // Quick indexes.
  const aiByCustomer = new Map<string, AIConversation>();
  for (const ai of Object.values(aiConversations)) {
    aiByCustomer.set(ai.customerId, ai);
  }

  const ringingIds = new Set(incomingCalls.map((c) => c.customerId));

  // Recent missed-call activity (voicemail / inbound-call without a follow-up).
  const recentMissedByCustomer = new Map<string, ActivityFeedEntry>();
  for (const a of activityFeed) {
    if (!a.customerId) continue;
    if (a.type === 'voicemail' || a.type === 'inbound-call') {
      // First-seen (feed is newest-first) wins for "most recent missed event".
      if (!recentMissedByCustomer.has(a.customerId)) {
        recentMissedByCustomer.set(a.customerId, a);
      }
    }
  }

  const items: ConversationFeedItem[] = [];

  for (const customer of Object.values(customers)) {
    const convo = customer.conversation;
    if (!convo || convo.length === 0) continue;

    // Skip very old / cold records. Active window keeps the inbox focused.
    const latest = convo[convo.length - 1]!;
    const latestT = new Date(latest.timestamp).getTime();
    if (now - latestT > activeWithinMs) continue;

    const ai = aiByCustomer.get(customer.id);
    const isLiveCall =
      activeCall?.customerId === customer.id && activeCall.status === 'live';
    const isRinging = ringingIds.has(customer.id);
    const recentMissed = recentMissedByCustomer.get(customer.id);

    // Resolve handler.
    let handler: ConversationHandler;
    if (ai) {
      handler = {
        kind: ai.channel === 'voice' ? 'ai-voice' : 'ai-intake',
        label: ai.channel === 'voice' ? 'AI Voice (Sara)' : 'AI Lead Intake',
      };
    } else if (latest.authorRole === 'agent' && latest.authorName) {
      handler = { kind: 'agent', label: latest.authorName };
    } else if (latest.direction === 'out' && latest.authorName) {
      handler = { kind: 'agent', label: latest.authorName };
    } else {
      handler = { kind: 'unassigned', label: 'Unassigned' };
    }

    // Resolve status (priority order matters).
    let status: ConversationStatus;
    if (isLiveCall) status = 'live-call';
    else if (isRinging) status = 'ringing-in';
    else if (ai?.state === 'hand-off-requested') status = 'ai-handoff';
    else if (
      recentMissed &&
      new Date(recentMissed.timestamp).getTime() > latestT - 60_000 &&
      isVoicemail(latest)
    )
      status = 'voicemail';
    else if (
      recentMissed &&
      latest.direction === 'in' &&
      !convo.some(
        (e) =>
          e.direction === 'out' &&
          new Date(e.timestamp).getTime() > new Date(recentMissed.timestamp).getTime(),
      )
    )
      status = 'missed-call';
    else if (ai && ai.state !== 'resolved') status = 'ai-active';
    else if (latest.direction === 'in') status = 'awaiting-agent';
    else if (latest.direction === 'out') status = 'awaiting-customer';
    else status = 'resolved';

    // Preview text — flatten the latest body.
    let previewBody = latest.body;
    if (!previewBody && latest.attachments?.length) {
      const att = latest.attachments[0]!;
      previewBody = att.kind === 'image' ? '📷 Image' : att.kind === 'audio' ? '🎧 Audio' : '📎 Attachment';
    }
    const preview = clip(previewBody || '(empty)', 110);

    items.push({
      customerId: customer.id,
      customer,
      latestEntry: latest,
      latestChannel: latest.channel,
      status,
      handler,
      lastActivityIso: latest.timestamp,
      preview,
      awaitingResponse: status === 'awaiting-agent' || status === 'ai-handoff' ||
        status === 'missed-call' || status === 'voicemail' || status === 'ringing-in',
      studioId: customer.primaryStudioId,
      inQueue: Boolean(sequenceState[customer.id]),
    });
  }

  // Sort: status priority first, then most-recent timestamp.
  items.sort((a, b) => {
    const pa = STATUS_PRIORITY[a.status];
    const pb = STATUS_PRIORITY[b.status];
    if (pa !== pb) return pa - pb;
    return b.lastActivityIso.localeCompare(a.lastActivityIso);
  });

  return items;
}

export interface StatusMeta {
  label: string;
  tone: 'live' | 'urgent' | 'ai' | 'warm' | 'idle';
}

export const STATUS_META: Record<ConversationStatus, StatusMeta> = {
  'live-call': { label: 'Live call', tone: 'live' },
  'ringing-in': { label: 'Ringing in', tone: 'live' },
  'ai-handoff': { label: 'AI hand-off', tone: 'urgent' },
  voicemail: { label: 'Voicemail', tone: 'urgent' },
  'missed-call': { label: 'Missed call', tone: 'urgent' },
  'ai-active': { label: 'AI on it', tone: 'ai' },
  'awaiting-agent': { label: 'Awaiting agent', tone: 'warm' },
  'awaiting-customer': { label: 'Awaiting customer', tone: 'idle' },
  resolved: { label: 'Resolved', tone: 'idle' },
};
